import { useEffect, useMemo, useRef, useState } from 'react';
import {
  catalogThumbForAssetId,
  normalizeAssetLookupKey,
} from '../utils/bookingAssetThumbnailCache';
import {
  bookingRecordAssetId,
  loadBookingListAssetThumbnails,
  uniqueBookingListAssetIdsForThumbs,
} from '../utils/bookingListDisplay';
import { registeredAssetThumbnailById } from '../utils/assetThumbnailStore';

export function useBookingListAssetThumbs(
  bookings: Record<string, unknown>[],
): Map<string, string | null> {
  const [thumbByAssetId, setThumbByAssetId] = useState<Map<string, string | null>>(() => new Map());
  const inFlightKeysRef = useRef<Set<string>>(new Set());
  const resolvedKeysRef = useRef<Set<string>>(new Set());
  const bookingsKey = useMemo(() => {
    try {
      return JSON.stringify(bookings.map((b) => bookingRecordAssetId(b) ?? b.booking_id ?? b.id));
    } catch {
      return String(bookings.length);
    }
  }, [bookings]);

  useEffect(() => {
    setThumbByAssetId((prev) => {
      let changed = false;
      const next = new Map(prev);
      for (const row of bookings) {
        const id = bookingRecordAssetId(row);
        if (!id) {
          continue;
        }
        const src = registeredAssetThumbnailById(id);
        if (!src) {
          continue;
        }
        for (const key of [id.toLowerCase(), normalizeAssetLookupKey(id)]) {
          if (next.get(key) !== src) {
            next.set(key, src);
            changed = true;
            resolvedKeysRef.current.add(key);
          }
        }
      }
      return changed ? next : prev;
    });
  }, [bookings, bookingsKey]);

  useEffect(() => {
    const skipKeys = new Set<string>();
    inFlightKeysRef.current.forEach((key) => skipKeys.add(key));
    resolvedKeysRef.current.forEach((key) => skipKeys.add(key));
    const needed = uniqueBookingListAssetIdsForThumbs(bookings, skipKeys);
    if (needed.length === 0) {
      return undefined;
    }

    for (const assetId of needed) {
      inFlightKeysRef.current.add(assetId.toLowerCase());
      inFlightKeysRef.current.add(normalizeAssetLookupKey(assetId));
    }

    let cancelled = false;
    void (async () => {
      const loaded = await loadBookingListAssetThumbnails(needed);
      if (cancelled) {
        for (const assetId of needed) {
          inFlightKeysRef.current.delete(assetId.toLowerCase());
          inFlightKeysRef.current.delete(normalizeAssetLookupKey(assetId));
        }
        return;
      }
      for (const assetId of needed) {
        const keys = [assetId.toLowerCase(), normalizeAssetLookupKey(assetId)];
        for (const key of keys) {
          inFlightKeysRef.current.delete(key);
        }
        const src =
          catalogThumbForAssetId(loaded, assetId) ??
          keys.map((key) => loaded.get(key)).find(Boolean) ??
          null;
        if (src) {
          for (const key of keys) {
            resolvedKeysRef.current.add(key);
          }
        }
      }
      setThumbByAssetId((prev) => {
        const next = new Map(prev);
        loaded.forEach((src, key) => {
          if (src) {
            next.set(key, src);
          }
        });
        return next;
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [bookings, bookingsKey]);

  return thumbByAssetId;
}
